import { useT } from "@transifex/react";
import type { ReactNode } from "react";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";
import ExerciseCard from "./ExerciseCard";

interface ExerciseWrapperProps {
    number: number;
    title: string;
    progress: number; // 0-100
    points?: number;
    children?: ReactNode;
    "data-testid"?: string;
}

const ExerciseWrapper = (props: ExerciseWrapperProps) => {
    const t = useT();
    const { getStyleBasedOnBreakpoint, isAboveMD } = useBreakpoints();

    const t_points = t("Points", { _tags: "OnlineTest" });
    const t_pointsAbbreviated = t("P.", { _tags: "OnlineTest" });

    /**
     * Desktop + children:    "5 Points"
     * Mobile + children:     "5 Points" (rendered outside the card)
     * Desktop + no children: "(5 Points)"
     * Mobile + no children:  "(5 P.)"
     */
    const formatPointsLabel = (): string | undefined => {
        if (props.points === undefined) return undefined;
        if (props.children) return `${props.points} ${t_points}`;
        if (isAboveMD) return `(${props.points} ${t_points})`;
        return `(${props.points} ${t_pointsAbbreviated})`;
    };

    if (props.children) {
        return (
            <div className="flex flex-col gap-s">
                {props.points !== undefined && (
                    <p
                        className={`text-l text-p6-grey-dark self-end ${getStyleBasedOnBreakpoint(
                            Breakpoint.MD,
                            "hidden",
                            "block"
                        )}`}
                    >
                        {`${props.points} ${t_points}`}
                    </p>
                )}
                <ExerciseCard
                    number={props.number}
                    title={props.title}
                    progress={props.progress}
                    pointsLabel={formatPointsLabel()}
                    data-testid={props["data-testid"]}
                >
                    {props.children}
                </ExerciseCard>
            </div>
        );
    }

    return (
        <ExerciseCard
            number={props.number}
            title={props.title}
            progress={props.progress}
            pointsLabel={formatPointsLabel()}
            data-testid={props["data-testid"]}
        />
    );
};

export default ExerciseWrapper;
